import React, { ComponentProps, FC, useEffect, useState } from "react";

import { Image } from "@UI/Components/Image/Image";
import { Skeleton } from "@UI/Components/Skeleton/Skeleton";

const RETRY_DELAY_MS = 1500;
const MAX_RETRIES = 40;

// A photo's server variant is generated in the background and 403s until it exists: keep a
// skeleton up, load the image out of sight beside it, and retry until it loads.
export const RetryingImage: FC<ComponentProps<typeof Image>> = ({
  src = "",
  boxSize,
  borderRadius,
  flexShrink,
  ...imageProps
}) => {
  const [retries, setRetries] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setRetries(0);
    setIsWaiting(false);
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!isWaiting) return undefined;

    const timer = setTimeout(() => {
      setRetries((count) => count + 1);
      setIsWaiting(false);
    }, RETRY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isWaiting]);

  const canRetry =
    Boolean(src) && !src.startsWith("blob:") && retries < MAX_RETRIES;
  const showsSkeleton = canRetry && !isLoaded;
  // A new URL each time, or the browser serves its remembered failure.
  const cacheBuster = src.includes("?") ? "&" : "?";

  return (
    <>
      {showsSkeleton && (
        <Skeleton
          boxSize={boxSize}
          borderRadius={borderRadius}
          flexShrink={flexShrink}
        />
      )}
      {!isWaiting && (
        <Image
          {...imageProps}
          src={retries ? `${src}${cacheBuster}retry=${retries}` : src}
          boxSize={boxSize}
          borderRadius={borderRadius}
          flexShrink={flexShrink}
          hidden={showsSkeleton}
          onLoad={() => setIsLoaded(true)}
          onError={() => canRetry && setIsWaiting(true)}
        />
      )}
    </>
  );
};
